import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, 
  Heart, 
  CheckCircle, 
  XCircle, 
  Clock, 
  MessageCircle,
  Loader2,
  Send,
  Inbox,
  Users
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api/vivah`;

export default function VivahInterestsPage({ user }) {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('received');
  const [interests, setInterests] = useState([]);
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);

  useEffect(() => {
    if (user) {
      if (activeTab === 'matches') {
        fetchMatches();
      } else {
        fetchInterests();
      }
    }
  }, [user, activeTab]);

  const fetchInterests = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('dharmbook_token');
      const endpoint = activeTab === 'received' ? 'received' : 'sent';
      const response = await axios.get(`${API}/interests/${endpoint}?token=${token}`);
      setInterests(response.data.interests || []);
    } catch (error) {
      console.error('Failed to fetch interests:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchMatches = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('dharmbook_token');
      const response = await axios.get(`${API}/interests/accepted?token=${token}`);
      setMatches(response.data.matches || []);
    } catch (error) {
      console.error('Failed to fetch matches:', error);
    } finally {
      setLoading(false);
    }
  };

  const respondToInterest = async (interestId, action) => {
    setActionLoading(interestId);
    try {
      const token = localStorage.getItem('dharmbook_token');
      await axios.put(`${API}/interest/${interestId}/respond?action=${action}&token=${token}`);
      
      // Update local state
      setInterests(prev => prev.map(i => 
        i.id === interestId 
          ? { ...i, status: action === 'accept' ? 'accepted' : 'declined' }
          : i
      ));
      
      toast.success(`Interest ${action}ed successfully!`);
      
      // Refresh if accepted to update matches count
      if (action === 'accept') {
        fetchInterests();
      }
    } catch (error) {
      toast.error('Failed to respond to interest');
    } finally {
      setActionLoading(null);
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffTime = Math.abs(now - date);
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    return date.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
  };

  const tabs = [
    { id: 'received', label: 'Received', icon: Inbox },
    { id: 'sent', label: 'Sent', icon: Send },
    { id: 'matches', label: 'Matches', icon: Users }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-rose-50 via-white to-amber-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-rose-600 to-amber-600 text-white py-4 px-4 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto flex items-center gap-4">
          <button onClick={() => navigate('/vivah')} className="p-2 hover:bg-white/10 rounded-full">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-xl font-bold">My Interests</h1>
            <p className="text-rose-200 text-sm">Manage your connections</p>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white border-b sticky top-16 z-10">
        <div className="max-w-4xl mx-auto px-4">
          <div className="flex">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex-1 py-4 flex items-center justify-center gap-2 border-b-2 transition-colors ${
                  activeTab === tab.id
                    ? 'border-rose-500 text-rose-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                <tab.icon className="w-5 h-5" />
                <span className="font-medium">{tab.label}</span>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 py-6">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 text-rose-600 animate-spin" />
          </div>
        ) : activeTab === 'matches' ? (
          // Matches Tab
          matches.length === 0 ? (
            <div className="text-center py-20">
              <Users className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-700 mb-2">No matches yet</h3>
              <p className="text-gray-500 mb-4">When you and someone mutually accept interest, they'll appear here</p>
              <button
                onClick={() => navigate('/vivah/matches')}
                className="px-6 py-2 bg-rose-500 text-white font-medium rounded-xl hover:bg-rose-600"
              >
                Browse Matches
              </button>
            </div>
          ) : (
            <div className="space-y-4">
              {matches.map((match) => (
                <div
                  key={match.interest_id}
                  className="bg-white rounded-2xl shadow-lg overflow-hidden"
                >
                  <div className="flex items-center p-4">
                    <div
                      onClick={() => navigate(`/vivah/profile/${match.profile.id}`)}
                      className="flex items-center gap-4 flex-1 cursor-pointer"
                    >
                      {/* Photo */}
                      <div className="w-20 h-20 rounded-xl overflow-hidden bg-gradient-to-br from-rose-100 to-amber-100 flex-shrink-0">
                        {match.profile.profile_photo ? (
                          <img 
                            src={match.profile.profile_photo} 
                            alt={match.profile.name}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <span className="text-2xl font-bold text-rose-300">
                              {match.profile.name?.charAt(0)}
                            </span>
                          </div>
                        )}
                      </div>
                      
                      {/* Info */}
                      <div className="flex-1">
                        <h3 className="font-bold text-gray-800">
                          {match.profile.name}, {match.profile.age}
                        </h3>
                        <p className="text-sm text-gray-500">
                          {match.profile.city}, {match.profile.state}
                        </p>
                        <p className="text-sm text-gray-500">
                          {match.profile.profession}
                        </p>
                        <div className="flex items-center gap-2 mt-1">
                          <CheckCircle className="w-4 h-4 text-green-500" />
                          <span className="text-xs text-green-600">
                            Matched {formatDate(match.matched_at)}
                          </span>
                        </div>
                      </div>
                    </div>
                    
                    {/* Chat Button */}
                    <button
                      onClick={() => toast.info('Chat feature coming soon!')}
                      className="px-4 py-2 bg-gradient-to-r from-blue-500 to-indigo-500 text-white font-medium rounded-xl hover:shadow-lg flex items-center gap-2"
                    >
                      <MessageCircle className="w-4 h-4" />
                      Chat
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )
        ) : (
          // Received/Sent Tab
          interests.length === 0 ? (
            <div className="text-center py-20">
              <Heart className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-700 mb-2">
                No {activeTab} interests
              </h3>
              <p className="text-gray-500 mb-4">
                {activeTab === 'received' 
                  ? "When someone expresses interest in your profile, it will appear here"
                  : "Interests you send to other profiles will appear here"
                }
              </p>
              <button
                onClick={() => navigate('/vivah/matches')}
                className="px-6 py-2 bg-rose-500 text-white font-medium rounded-xl hover:bg-rose-600"
              >
                Browse Matches
              </button>
            </div>
          ) : (
            <div className="space-y-4">
              {interests.map((interest) => (
                <div
                  key={interest.id}
                  className="bg-white rounded-2xl shadow-lg overflow-hidden"
                >
                  <div className="flex items-center p-4">
                    <div
                      onClick={() => navigate(`/vivah/profile/${interest.profile.id}`)}
                      className="flex items-center gap-4 flex-1 cursor-pointer"
                    >
                      {/* Photo */}
                      <div className="w-20 h-20 rounded-xl overflow-hidden bg-gradient-to-br from-rose-100 to-amber-100 flex-shrink-0">
                        {interest.profile.profile_photo ? (
                          <img 
                            src={interest.profile.profile_photo} 
                            alt={interest.profile.name}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <span className="text-2xl font-bold text-rose-300">
                              {interest.profile.name?.charAt(0)}
                            </span>
                          </div>
                        )}
                      </div>
                      
                      {/* Info */}
                      <div className="flex-1">
                        <h3 className="font-bold text-gray-800">
                          {interest.profile.name}, {interest.profile.age}
                        </h3>
                        <p className="text-sm text-gray-500">
                          {interest.profile.city}, {interest.profile.state}
                        </p>
                        <p className="text-sm text-gray-500">
                          {interest.profile.profession} • {interest.profile.education}
                        </p>
                        <div className="flex flex-wrap gap-2 mt-1">
                          <span className="text-xs bg-rose-50 text-rose-700 px-2 py-0.5 rounded-full">
                            {interest.profile.religion}
                          </span>
                          {interest.profile.caste && (
                            <span className="text-xs bg-amber-50 text-amber-700 px-2 py-0.5 rounded-full">
                              {interest.profile.caste}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    
                    {/* Actions */}
                    <div className="flex flex-col items-end gap-2">
                      <span className="text-xs text-gray-400">{formatDate(interest.created_at)}</span>
                      
                      {interest.status === 'pending' && activeTab === 'received' && (
                        <div className="flex gap-2">
                          <button
                            onClick={() => respondToInterest(interest.id, 'accept')}
                            disabled={actionLoading === interest.id}
                            className="px-3 py-1.5 bg-green-500 text-white text-sm font-medium rounded-lg hover:bg-green-600 disabled:opacity-50 flex items-center gap-1"
                          >
                            {actionLoading === interest.id ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <>
                                <CheckCircle className="w-4 h-4" />
                                Accept
                              </>
                            )}
                          </button>
                          <button
                            onClick={() => respondToInterest(interest.id, 'decline')}
                            disabled={actionLoading === interest.id}
                            className="px-3 py-1.5 bg-gray-200 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-300 disabled:opacity-50 flex items-center gap-1"
                          >
                            <XCircle className="w-4 h-4" />
                            Decline
                          </button>
                        </div>
                      )}
                      
                      {interest.status === 'pending' && activeTab === 'sent' && (
                        <span className="px-3 py-1 bg-amber-100 text-amber-700 text-sm font-medium rounded-full flex items-center gap-1">
                          <Clock className="w-4 h-4" />
                          Pending
                        </span>
                      )}
                      
                      {interest.status === 'accepted' && (
                        <span className="px-3 py-1 bg-green-100 text-green-700 text-sm font-medium rounded-full flex items-center gap-1">
                          <CheckCircle className="w-4 h-4" />
                          Accepted
                        </span>
                      )}
                      
                      {interest.status === 'declined' && (
                        <span className="px-3 py-1 bg-gray-100 text-gray-600 text-sm font-medium rounded-full flex items-center gap-1">
                          <XCircle className="w-4 h-4" />
                          Declined
                        </span>
                      )}
                    </div>
                  </div>
                  
                  {/* Message if any */}
                  {interest.message && (
                    <div className="px-4 pb-4">
                      <p className="text-sm text-gray-600 bg-gray-50 p-3 rounded-xl">
                        "{interest.message}"
                      </p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )
        )}
      </div>
    </div>
  );
}
